import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Building2, Users, Package, Calendar, AlertTriangle, Send, Eye, SquarePen } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TenantContractFields } from "@/components/TenantContractFields";
import { PasswordVisibilityField } from "@/components/PasswordVisibilityField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}

function formatEuro(v) {
  if (v == null || v === "") return "—";
  try { return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v); }
  catch { return `€ ${v}`; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const d = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
    return d;
  } catch { return null; }
}

function StatusBadge({ status, days }) {
  const map = {
    active: { bg: "bg-[#E9F0EA]", fg: "text-[#4A6B53]", label: "Attivo" },
    suspended: { bg: "bg-[#FBF1E4]", fg: "text-[#D4A373]", label: "Sospeso" },
    expired: { bg: "bg-[#F5E4E4]", fg: "text-[#B24040]", label: "Scaduto" },
  };
  const s = map[status] || map.active;
  const isExpiringSoon = status === "active" && days !== null && days < 30 && days >= 0;
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase rounded-full ${s.bg} ${s.fg}`}>
        {s.label}
      </span>
      {isExpiringSoon && (
        <span className="inline-flex items-center gap-1 text-[10px] text-[#D4A373]">
          <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> {days}gg
        </span>
      )}
    </div>
  );
}

export default function SuperAdmin() {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [credentialsTenant, setCredentialsTenant] = useState(null);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [impersonatingTenant, setImpersonatingTenant] = useState(null);
  const [form, setForm] = useState({
    name: "", company_name: "", vat_number: "", phone: "",
    tax_code: "", pec_email: "", address: "", cap: "", city: "", province: "",
    administrator_full_name: "",
    admin_email: "", admin_password: "",
  });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tenants`);
      setTenants(r.data || []);
    } catch (e) { toast.error("Errore caricamento abbonamenti"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.admin_email.trim() || !form.admin_password.trim()) {
      toast.error("Nome, email e password admin obbligatori"); return;
    }
    setCreating(true);
    let createdId = null;
    try {
      const t = await axios.post(`${API}/tenants`, {
        ...form, email: form.admin_email.trim(), admin_email: undefined, admin_password: undefined,
      });
      createdId = t.data.id;
      await axios.post(`${API}/tenants/${t.data.id}/users`, {
        email: form.admin_email, password: form.admin_password, role: "tenant_admin",
      });
      toast.success("Abbonamento creato");
      setShowCreate(false);
      setForm({ name:"", company_name:"", vat_number:"", phone:"",
        tax_code:"", pec_email:"", address:"", cap:"", city:"", province:"",
        administrator_full_name:"", admin_email:"", admin_password:"" });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string" ? detail : Array.isArray(detail)
        ? detail.map(item => item.msg).join(", ") : detail?.message || "Errore creazione";
      toast.error(createdId ? `Abbonamento creato ma senza amministratore: ${message}` : message);
      if (createdId) {
        setShowCreate(false);
        navigate(`/admin/tenants/${createdId}`);
      }
    } finally { setCreating(false); }
  };

  const sendCredentials = async () => {
    if (!credentialsTenant || sendingCredentials) return;
    setSendingCredentials(true);
    try {
      await axios.post(`${API}/tenants/${credentialsTenant.id}/send-credentials`);
      toast.success("Credenziali inviate all'email del primo admin");
      setCredentialsTenant(null);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Email non inviata. Riprova.");
      await load();
    } finally { setSendingCredentials(false); }
  };

  const impersonate = async (tenantId) => {
    if (impersonatingTenant) return;
    setImpersonatingTenant(tenantId);
    try {
      await startImpersonation(tenantId);
      navigate("/products");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Impossibile entrare nella vista cliente.");
    } finally { setImpersonatingTenant(null); }
  };

  return (
    <div className="p-8 md:p-12" data-testid="superadmin-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8579]">Gestione abbonamenti</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-2">Abbonamenti attivi</h1>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1A1A1A] hover:bg-black text-white" data-testid="create-tenant-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Nuovo abbonamento
        </Button>
      </div>


      {loading ? (
        <div className="text-sm text-[#8A8579]">Caricamento...</div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-[#E5E5E0] rounded">
          <Building2 className="w-8 h-8 mx-auto text-[#8A8579] mb-4" strokeWidth={1.2} />
          <div className="font-serif text-2xl text-[#1A1A1A]">Nessun abbonamento</div>
          <p className="text-sm text-[#8A8579] mt-2">Crea il primo abbonamento per iniziare.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-sm overflow-x-auto">
          <table className="w-full min-w-[1020px] text-sm" data-testid="tenants-table">
            <thead className="bg-[#F5F2EC] border-b border-[#E5E5E0]">
              <tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#8A8579]">
                <th className="px-5 py-3">Azienda</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3">Scadenza</th>
                <th className="px-5 py-3">Utenti</th>
                <th className="px-5 py-3">Prodotti</th>
                <th className="px-5 py-3">Brand</th>
                <th className="px-5 py-3">Prezzo</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => {
                const d = daysUntil(t.subscription_end);
                const expired = t.status === "expired" || (t.status === "active" && d !== null && d < 0);
                const expiringSoon = t.status === "active" && d !== null && d >= 0 && d < 30;
                const rowClass = expired
                  ? "bg-[#FBEEEE] border-l-[3px] border-l-[#B24040]"
                  : expiringSoon
                    ? "bg-[#FBF6EE] border-l-[3px] border-l-[#D4A373]"
                    : "hover:bg-[#FAF9F6]";
                return (
                  <tr key={t.id} className={`border-b border-[#F5F2EC] last:border-0 ${rowClass}`} data-testid={`tenant-row-${t.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-serif text-lg text-[#1A1A1A]">{t.name}</div>
                      {t.company_name && t.company_name !== t.name && (
                        <div className="text-[11px] text-[#8A8579]">{t.company_name}</div>
                      )}
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={t.status} days={d} /></td>
                    <td className="px-5 py-4 text-[#1A1A1A]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-[#8A8579]" strokeWidth={1.5} />
                        {formatDate(t.subscription_end)}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 text-[#1A1A1A]">
                        <Users className="w-3.5 h-3.5 text-[#8A8579]" strokeWidth={1.5} />
                        {t.stats?.users || 0}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 text-[#1A1A1A]" data-testid={`tenant-products-count-${t.id}`}>
                        <Package className="w-3.5 h-3.5 text-[#8A8579]" strokeWidth={1.5} />
                        {t.stats?.products || 0} <span className="text-[#8A8579]">/ {t.max_products ? t.max_products : "∞"}</span>
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[#1A1A1A]" data-testid={`tenant-brands-count-${t.id}`}>
                      {t.stats?.brands || 0} <span className="text-[#8A8579]">/ {t.max_brands ? t.max_brands : "∞"}</span>
                    </td>
                    <td className="px-5 py-4 text-[#1A1A1A]" data-testid={`tenant-price-${t.id}`}>{formatEuro(t.agreed_price)}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button type="button" onClick={() => impersonate(t.id)} disabled={!!impersonatingTenant}
                        className="inline-flex items-center gap-1.5 mr-5 text-[10px] tracking-[0.15em] uppercase text-[#111111] hover:text-[#525252] disabled:opacity-50"
                        data-testid={`tenant-impersonate-${t.id}`}>
                        <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />{impersonatingTenant === t.id ? "Accesso..." : "Impersona"}
                      </button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setCredentialsTenant(t)}
                        disabled={!t.credential_delivery_available}
                        title={t.credential_delivery_available ? "Invia le credenziali al primo admin" : "Password già cambiata o non disponibile: reimpostala dalla scheda cliente"}
                        className="mr-5 rounded-none border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white disabled:text-[#525252] disabled:cursor-not-allowed"
                        data-testid={`tenant-send-credentials-${t.id}`}>
                        <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="flex flex-col items-start text-[10px] leading-[12px]">
                          <span>Invia</span><span>Credenziali</span>
                        </span>
                      </Button>
                      <Button asChild variant="outline" size="sm" className="rounded-none border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white" >
                        <Link to={`/admin/tenants/${t.id}`} data-testid={`tenant-manage-${t.id}`}>
                          <SquarePen className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Gestisci
                        </Link>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!credentialsTenant} onOpenChange={(open) => { if (!open && !sendingCredentials) setCredentialsTenant(null); }}>
        <DialogContent data-testid="send-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Inviare le credenziali?</DialogTitle>
            <DialogDescription className="text-sm text-[#525252]" data-testid="send-credentials-recipient">
              Invieremo all’email aziendale di {credentialsTenant?.name} il riepilogo anagrafico e la password provvisoria per il primo accesso.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={sendingCredentials}
              onClick={() => setCredentialsTenant(null)} data-testid="send-credentials-cancel">Annulla</Button>
            <Button type="button" onClick={sendCredentials} disabled={sendingCredentials}
              className="bg-[#171717] text-white hover:bg-[#262626]"
              data-testid="send-credentials-confirm">
              <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />{sendingCredentials ? "Invio in corso..." : "Invia email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[92dvh] overflow-y-auto" data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Nuovo abbonamento</DialogTitle>
            <DialogDescription className="sr-only">Anagrafica e credenziali del primo amministratore.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-5 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8A8579]">Nome commerciale *</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Es. A&J Biocosmetics" data-testid="new-tenant-name" />
            </div>
            <div className="border-t border-[#E5E5E0] pt-5">
              <p className="text-xs uppercase tracking-widest text-[#8A8579] mb-4">Dati per il contratto</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TenantContractFields values={form} prefix="new-tenant"
                  onChange={(key, value)=>setForm(prev=>({...prev, [key]:value}))} />
              </div>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Telefono</Label>
              <Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} data-testid="new-tenant-phone" /></div>
            <div className="pt-4 border-t border-[#E5E5E0]">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8579] mb-3">Primo account admin</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8A8579]">Email aziendale e admin *</Label>
                  <Input type="email" value={form.admin_email} onChange={(e)=>setForm({...form, admin_email:e.target.value})} required data-testid="new-tenant-admin-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8A8579]">Password *</Label>
                  <PasswordVisibilityField value={form.admin_password} onChange={(e)=>setForm({...form, admin_password:e.target.value})}
                    required minLength={6} autoComplete="new-password" testId="new-tenant-admin-password" />
                </div>
              </div>
              <p className="text-[11px] text-[#8A8579] mt-2">Dopo la creazione, usa «Invia credenziali di accesso» nella lista. Al primo accesso il cliente creerà una password personale.</p>
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={()=>setShowCreate(false)} data-testid="new-tenant-cancel">Annulla</Button>
              <Button type="submit" disabled={creating} className="bg-[#1A1A1A] text-white hover:bg-black" data-testid="new-tenant-submit">
                {creating ? "Creazione..." : "Crea abbonamento"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
